import { Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@akuna/ui";
import type { CategoryRevenueRow } from "@/hooks/useRevenueReport";
import { exportToExcel } from "@/lib/utils/export";

interface RevenueCategoryMarginProps {
  data: CategoryRevenueRow[];
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
}

export function RevenueCategoryMargin({ data }: RevenueCategoryMarginProps) {
  const { t } = useTranslation("reports");

  function handleExport() {
    exportToExcel({
      rows: data.map((r) => ({
        [t("category")]: r.name,
        [t("revenue")]: r.revenue,
        [t("percentage")]: `${r.pct.toFixed(1)}%`,
      })),
      sheetName: t("categoryMarginTitle"),
      fileName: "faturamento-por-categoria.xlsx",
    });
  }

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">{t("categoryMarginTitle")}</CardTitle>
            <CardDescription>{t("categoryMarginDesc")}</CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={data.length === 0}
          >
            <Download className="h-4 w-4 mr-1" />
            {t("export")}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 flex-1">
        {data.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">{t("emptyBreakdown")}</p>
        ) : (
          data.map((row) => (
            <div key={row.id} className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span
                    className="h-2.5 w-2.5 rounded-full shrink-0"
                    style={{ background: row.color }}
                  />
                  <span className="text-sm font-medium truncate">{row.name}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-sm font-semibold">{formatCurrency(row.revenue)}</span>
                  <span className="text-xs text-muted-foreground w-10 text-right">
                    {row.pct.toFixed(1)}%
                  </span>
                </div>
              </div>
              <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${Math.max(row.pct, 2)}%`,
                    background: row.color,
                  }}
                />
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
